/**
 * ReportProblemModal.tsx
 * نموذج "الإبلاغ عن مشكلة" — نصّ حرّ + مرفق اختياري (صورة/فيديو قصير)، يصل مباشرةً
 * إلى لوحة الأدمن (admin.nav.reports). السياق التقني (الصفحة، المتصفّح، النظام) يُجمَع
 * تلقائياً في الخادم من الطلب — لا يُطلَب من المستخدم كتابته، أسلوب Windy/Zoom Earth.
 */
import { useRef, useState } from 'react';
import { FiPaperclip, FiX } from 'react-icons/fi';
import { useTranslation } from '../../i18n/LanguageContext';

const MAX_DESCRIPTION = 500;
const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024; // 10MB — يطابق الحدّ في الخادم

interface Props {
    onClose: () => void;
}

export function ReportProblemModal({ onClose }: Props) {
    const { t } = useTranslation();
    const [description, setDescription] = useState('');
    const [attachment, setAttachment] = useState<File | null>(null);
    const [attachmentError, setAttachmentError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const pickFile = (file: File | undefined) => {
        setAttachmentError(null);
        if (!file) { setAttachment(null); return; }
        if (file.size > MAX_ATTACHMENT_BYTES) {
            setAttachmentError(t('report.attachmentTooLarge'));
            setAttachment(null);
            return;
        }
        setAttachment(file);
    };

    const submit = async () => {
        if (!description.trim() || submitting) return;
        setSubmitting(true);
        setError(null);
        try {
            const form = new FormData();
            form.append('description', description.trim());
            form.append('page_url', window.location.href);
            if (attachment) form.append('attachment', attachment);

            const res = await fetch('/api/v1/report-problem', { method: 'POST', body: form });
            if (!res.ok) throw new Error(String(res.status));
            setDone(true);
        } catch {
            setError(t('report.submitFailed'));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-[8px] flex items-center justify-center z-[2000] p-5 animate-fade-in" onClick={onClose}>
            <div className="w-full max-w-[440px] bg-[var(--panel-bg)] border border-[var(--panel-border)] text-inherit light:text-[var(--text-primary)] rounded-2xl overflow-hidden flex flex-col animate-fade-in-up" onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-between items-center px-[22px] py-[18px] border-b border-[var(--divider)]">
                    <h2 className="m-0 text-lg font-extrabold">{t('report.title')}</h2>
                    <button className="light:text-[var(--text-secondary)]" onClick={onClose} aria-label={t('common.close')}><FiX /></button>
                </div>

                {done ? (
                    <div className="px-[22px] pt-7 pb-[26px] flex flex-col items-center gap-4 text-center">
                        <p className="m-0 text-sm leading-[1.7]">{t('report.thanks')}</p>
                        <button className="px-4 py-[11px] rounded-[10px] border-none bg-[#c9a227] text-[#1a1a1a] text-sm font-extrabold cursor-pointer transition-[filter] duration-150 ease hover:not-disabled:brightness-[1.08] disabled:opacity-50 disabled:cursor-not-allowed" onClick={onClose}>{t('common.close')}</button>
                    </div>
                ) : (
                    <div className="px-[22px] pt-[18px] pb-[22px] flex flex-col gap-3">
                        <p className="m-0 text-[13px] opacity-75 leading-[1.6]">{t('report.hint')}</p>
                        <div className="relative">
                            <textarea
                                className="w-full resize-y min-h-[110px] pt-3 px-[14px] pb-[22px] rounded-xl border border-[var(--border-color)] bg-white/[0.04] text-inherit text-[13px] leading-[1.6] outline-none focus:border-[var(--color-primary)]"
                                value={description}
                                maxLength={MAX_DESCRIPTION}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder={t('report.placeholder')}
                                rows={5}
                            />
                            <span className="absolute end-3 bottom-2 text-[11px] opacity-50 pointer-events-none">{description.length} / {MAX_DESCRIPTION}</span>
                        </div>

                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/png,image/jpeg,image/webp,image/gif,video/mp4,video/webm"
                            hidden
                            onChange={(e) => pickFile(e.target.files?.[0])}
                        />
                        <button className="inline-flex items-center gap-2 self-start px-[14px] py-2 rounded-[10px] border border-dashed border-[var(--border-color)] bg-transparent text-inherit text-[12.5px] font-semibold cursor-pointer max-w-full overflow-hidden text-ellipsis whitespace-nowrap hover:bg-white/[0.06]" onClick={() => fileInputRef.current?.click()} type="button">
                            <FiPaperclip /> {attachment ? attachment.name : t('report.attach')}
                        </button>
                        {attachmentError && <p className="m-0 text-[#e05a5a] text-[12.5px] font-semibold">{attachmentError}</p>}

                        {error && <p className="m-0 text-[#e05a5a] text-[12.5px] font-semibold">{error}</p>}

                        <button
                            className="px-4 py-[11px] rounded-[10px] border-none bg-[#c9a227] text-[#1a1a1a] text-sm font-extrabold cursor-pointer transition-[filter] duration-150 ease hover:not-disabled:brightness-[1.08] disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={submit}
                            disabled={!description.trim() || submitting}
                            type="button"
                        >
                            {submitting ? t('common.loading') : t('report.submit')}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
